/**
 * MONSTER TOWN — THE NUDGE SYSTEM. HUD/AUDIO/TOUCH CREW (src/ui/**).
 *
 * A gentle "what to do" that NEVER nags. The whole design of this file is about
 * the things it refuses to do:
 *
 *  - It never blocks input. The bubble is `pointer-events: none`; the game keeps
 *    playing underneath it.
 *  - It never fires while the kid is doing something. Any tap clears it, and the
 *    idle clock restarts.
 *  - It shows a given hint at most `maxShows` times EVER (persisted), so the
 *    second week of play is not the first week of tutorial.
 *  - It leaves on its own after a few seconds. A hint you have to dismiss is a
 *    modal, and a modal is a dead end.
 *  - There is a hard cooldown between any two nudges, so they can never queue up
 *    into a stream of advice.
 *  - It is NOT a difficulty setting and NOT an age mode (LAW #3) — every kid
 *    gets the same hints, and doing the thing makes them stop.
 *
 * RULES ARE DATA. A new hint is an entry in `RULES`, never a change below it.
 */

import { bus } from '../core/events'
import { engine } from '../core/engine'
import { wallet } from '../core/economy'
import { world } from '../core/scene'
import { app } from '../app/context'
import type { NudgeAnchor } from './events'
import { el } from './dom'
import { iconEl } from './icons'

export interface NudgeRule {
  readonly id: string
  readonly icon: string
  readonly at: NudgeAnchor
  /** Seconds of no interaction before this becomes eligible. */
  readonly idleSeconds: number
  /** Lifetime cap. Small on purpose. */
  readonly maxShows: number
  /** True when the hint is still relevant. Cheap — polled twice a second. */
  readonly when: () => boolean
}

const SHOW_SECONDS = 6.5
const COOLDOWN_SECONDS = 30

function placementsHere(): number {
  const id = world.activeSceneId
  if (id === null) return 0
  return world.get(id)?.grid.count() ?? 0
}

/** DATA. Add a hint here; the machinery below never changes. */
const RULES: readonly NudgeRule[] = [
  {
    id: 'pickPiece',
    icon: 'block',
    at: 'drawer',
    idleSeconds: 5,
    maxShows: 3,
    when: () => app.mode === 'build' && selected() === null,
  },
  {
    id: 'firstBuild',
    icon: 'hammer',
    at: 'mode',
    idleSeconds: 14,
    maxShows: 3,
    when: () => app.mode === 'play' && placementsHere() < 2,
  },
  {
    id: 'tapThings',
    icon: 'tap',
    at: 'centre',
    idleSeconds: 20,
    maxShows: 2,
    when: () => app.mode === 'play' && placementsHere() >= 2 && !interactedThisSession,
  },
  {
    id: 'goShopping',
    icon: 'bag',
    at: 'shop',
    idleSeconds: 16,
    maxShows: 2,
    when: () => !shopOpenedThisSession && wallet.shopCatalogue().some((c) => !c.owned && c.affordable),
  },
]

let interactedThisSession = false
let shopOpenedThisSession = false
let selectedGetter: () => string | null = () => null
function selected(): string | null {
  return selectedGetter()
}

export interface NudgeApi {
  /** Elements the bubble can point at. 'centre' needs no element. */
  setAnchor(name: NudgeAnchor, node: HTMLElement): void
  /** Serialised show-counts for the `ui` save section. */
  counts(): Record<string, number>
  restore(counts: Record<string, number>): void
  clear(): void
  /** Force one for screenshots/QA. Bypasses the idle clock, not the anchors. */
  force(id: string): boolean
}

export function installNudges(
  hud: HTMLElement,
  opts: { selectedPlaceable: () => string | null },
): NudgeApi {
  selectedGetter = opts.selectedPlaceable
  const shown = new Map<string, number>()
  const anchors = new Map<NudgeAnchor, HTMLElement>()

  const bubble = el('div', 'mt-nudge')
  bubble.style.display = 'none'
  hud.append(bubble)

  let idle = 0
  let visibleFor = 0
  let cooldown = 4 // a few seconds of grace right after boot
  let current: string | null = null

  const hideBubble = (): void => {
    if (current === null) return
    bus.emit('ui:nudgeCleared', { id: current })
    current = null
    bubble.style.display = 'none'
    visibleFor = 0
    cooldown = COOLDOWN_SECONDS
  }

  const place = (at: NudgeAnchor): void => {
    const hudRect = hud.getBoundingClientRect()
    if (at === 'centre') {
      bubble.style.left = '50%'
      bubble.style.top = '38%'
      bubble.style.right = 'auto'
      bubble.style.bottom = 'auto'
      bubble.style.transform = 'translate(-50%, -50%)'
      bubble.dataset['point'] = 'down'
      return
    }
    const node = anchors.get(at)
    if (node === undefined) {
      bubble.style.left = '50%'
      bubble.style.top = '38%'
      bubble.style.transform = 'translate(-50%, -50%)'
      bubble.dataset['point'] = 'down'
      return
    }
    const r = node.getBoundingClientRect()
    const cx = r.left + r.width / 2 - hudRect.left
    const cy = r.top + r.height / 2 - hudRect.top
    const onRight = cx > hudRect.width * 0.6
    const onBottom = cy > hudRect.height * 0.6
    bubble.style.transform = 'none'
    bubble.style.right = 'auto'
    bubble.style.bottom = 'auto'
    if (onBottom) {
      // Sit above the control and point down at it.
      bubble.style.left = `${Math.max(12, Math.min(cx - 110, hudRect.width - 240))}px`
      bubble.style.top = `${Math.max(12, cy - r.height / 2 - 108)}px`
      bubble.dataset['point'] = 'down'
    } else {
      // Sit beside the control and point sideways at it.
      bubble.style.top = `${Math.max(12, cy - 46)}px`
      if (onRight) {
        bubble.style.left = `${Math.max(12, cx - r.width / 2 - 270)}px`
        bubble.dataset['point'] = 'right'
      } else {
        bubble.style.left = `${cx + r.width / 2 + 22}px`
        bubble.dataset['point'] = 'left'
      }
    }
  }

  /**
   * A NUDGE POINTS; IT DOES NOT REPEAT THE THING IT IS POINTING AT (fixed r2).
   *
   * The bubble drew the RULE'S icon, and the rules name the icon of the control
   * they are anchored to — so the `firstBuild` hint put a hammer in a bubble
   * eight pixels above the hammer button, tail pointing down at it. Captured
   * bottom-right in 09-town-close.png and 10-town-wide-full.png, it reads as two
   * hammer buttons stacked, and the one on top does nothing when you tap it
   * (the bubble is `pointer-events: none`). The picture that was supposed to say
   * "press that" said "here is another one of those".
   *
   * A bubble anchored to a control now shows the POINTING FINGER and nothing
   * else: the tail already says which control, and the finger says press it.
   * The rule's own picture is only used for a `centre` hint, which has no
   * control under it to duplicate.
   */
  const show = (rule: NudgeRule): void => {
    bubble.replaceChildren(iconEl(rule.at === 'centre' ? rule.icon : 'tap', 56))
    bubble.dataset['rule'] = rule.id
    bubble.style.display = 'flex'
    place(rule.at)
    current = rule.id
    visibleFor = 0
    shown.set(rule.id, (shown.get(rule.id) ?? 0) + 1)
    bus.emit('ui:nudgeShown', { id: rule.id })
    bus.emit('audio:cue', { cue: 'nudge' })
  }

  // Any interaction at all resets the clock and takes the hint away. The kid is
  // clearly fine; the hint has nothing to add.
  const poke = (): void => {
    idle = 0
    hideBubble()
  }
  bus.on('input:tap', poke)
  bus.on('input:dragStart', poke)
  bus.on('input:hold', poke)
  bus.on('build:placed', poke)
  bus.on('app:modeChanged', poke)
  bus.on('play:interacted', () => {
    interactedThisSession = true
    poke()
  })
  bus.on('ui:panel', (e) => {
    if (e.panel === 'shop') shopOpenedThisSession = true
    poke()
  })
  // Explicit request from another crew. Still subject to the caps.
  bus.on('ui:nudge', (e) => {
    if (current !== null || cooldown > 0) return
    if ((shown.get(e.id) ?? 0) >= 2) return
    show({ id: e.id, icon: e.icon, at: e.at, idleSeconds: 0, maxShows: 2, when: () => true })
  })

  engine.addSystem({
    id: 'ui.nudge',
    order: 92,
    fixed: (dt) => {
      idle += dt
      if (cooldown > 0) cooldown -= dt
      if (current !== null) {
        visibleFor += dt
        if (visibleFor >= SHOW_SECONDS) hideBubble()
        return
      }
      if (cooldown > 0) return
      // Panels own the screen while open; a hint over a panel is noise.
      if (app.phase === 'monsterCreator' || app.phase === 'slotPick' || app.phase === 'photo') return
      for (const rule of RULES) {
        if (idle < rule.idleSeconds) continue
        if ((shown.get(rule.id) ?? 0) >= rule.maxShows) continue
        let ok = false
        try {
          ok = rule.when()
        } catch {
          ok = false
        }
        if (!ok) continue
        show(rule)
        return
      }
    },
  })

  return {
    setAnchor: (name, node) => anchors.set(name, node),
    counts: () => Object.fromEntries(shown),
    restore: (counts) => {
      shown.clear()
      for (const [k, v] of Object.entries(counts)) {
        if (Number.isFinite(v)) shown.set(k, Math.max(0, Math.floor(v)))
      }
    },
    clear: hideBubble,
    force: (id) => {
      const rule = RULES.find((r) => r.id === id)
      if (rule === undefined) return false
      cooldown = 0
      show(rule)
      return true
    },
  }
}
